import { supabase } from '../lib/supabase';

export const visitorService = {
  async registerVisitor(galleryId, visitorData) {
    try {
      // Check if visitor already registered for this gallery
      const { data: existing, error: checkError } = await supabase?.from('visitor_registrations')?.select('*')?.eq('gallery_id', galleryId)?.eq('visitor_email', visitorData?.email)?.single();

      if (existing) {
        // Update existing registration
        const { data, error } = await supabase?.from('visitor_registrations')?.update({
            last_visit_at: new Date()?.toISOString(),
            visit_count: existing?.visit_count + 1
          })?.eq('id', existing?.id)?.select()?.single();

        if (error) throw error;
        return data;
      }

      // Create new registration
      const { data, error } = await supabase?.from('visitor_registrations')?.insert({
          gallery_id: galleryId,
          visitor_name: visitorData?.name,
          visitor_email: visitorData?.email,
          visitor_phone: visitorData?.phone
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error registering visitor:', error);
      throw error;
    }
  },

  async checkVisitorRegistration(galleryId, email) {
    try {
      const { data, error } = await supabase?.from('visitor_registrations')?.select('*')?.eq('gallery_id', galleryId)?.eq('visitor_email', email)?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      return data;
    } catch (error) {
      console.error('Error checking visitor registration:', error);
      return null;
    }
  },

  async getGalleryVisitors(galleryId) {
    try {
      const { data, error } = await supabase?.from('visitor_registrations')?.select('*')?.eq('gallery_id', galleryId)?.order('registered_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching gallery visitors:', error);
      throw error;
    }
  },

  async getAllVisitors() {
    try {
      const { data, error } = await supabase?.from('visitor_registrations')?.select(`
          *,
          galleries!visitor_registrations_gallery_id_fkey(gallery_name)
        `)?.order('registered_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching all visitors:', error);
      throw error;
    }
  },

  async getVisitorEngagementMetrics() {
    try {
      const { data: visitors, error: visitorError } = await supabase?.from('visitor_registrations')?.select('*');
      
      if (visitorError) throw visitorError;

      const { data: media, error: mediaError } = await supabase?.from('media')?.select('view_count, download_count');
      
      if (mediaError) throw mediaError;

      const totalVisitors = visitors?.length || 0;
      const totalDownloads = media?.reduce((sum, m) => sum + (m?.download_count || 0), 0) || 0;
      const totalViews = media?.reduce((sum, m) => sum + (m?.view_count || 0), 0) || 0;
      const totalVisits = visitors?.reduce((sum, v) => sum + (v?.visit_count || 0), 0) || 0;

      return {
        totalVisitors,
        totalDownloads,
        totalViews,
        totalVisits,
        avgVisitsPerVisitor: totalVisitors > 0 ? (totalVisits / totalVisitors)?.toFixed(1) : 0,
        avgDownloadsPerVisitor: totalVisitors > 0 ? (totalDownloads / totalVisitors)?.toFixed(1) : 0
      };
    } catch (error) {
      console.error('Error fetching engagement metrics:', error);
      throw error;
    }
  },

  async getDownloadTrends(days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const { data, error } = await supabase?.from('media')?.select('download_count, created_at, updated_at')?.gte('updated_at', startDate?.toISOString());

      if (error) throw error;

      // Group by date
      const trendMap = {};
      const today = new Date();
      
      // Initialize all dates with 0
      for (let i = 0; i < days; i++) {
        const date = new Date(today);
        date?.setDate(date?.getDate() - i);
        const dateKey = date?.toISOString()?.split('T')?.[0];
        trendMap[dateKey] = 0;
      }

      // Aggregate downloads (simplified - in production you'd track individual download events)
      data?.forEach(item => {
        const dateKey = new Date(item?.updated_at)?.toISOString()?.split('T')?.[0];
        if (trendMap?.[dateKey] !== undefined) {
          trendMap[dateKey] += item?.download_count || 0;
        }
      });

      // Convert to array and sort
      const trends = Object.entries(trendMap)?.map(([date, downloads]) => ({ date, downloads }))?.sort((a, b) => new Date(a.date) - new Date(b.date));

      return trends;
    } catch (error) {
      console.error('Error fetching download trends:', error);
      throw error;
    }
  },

  async exportVisitorData() {
    try {
      const { data: visitors, error: visitorError } = await supabase?.from('visitor_registrations')?.select(`
          *,
          galleries!visitor_registrations_gallery_id_fkey(gallery_name, client_name)
        `)?.order('registered_at', { ascending: false });

      if (visitorError) throw visitorError;

      // Transform data for export
      const exportData = visitors?.map(v => ({
        visitorName: v?.visitor_name,
        visitorEmail: v?.visitor_email,
        visitorPhone: v?.visitor_phone,
        galleryName: v?.galleries?.gallery_name,
        clientName: v?.galleries?.client_name,
        registeredAt: new Date(v?.registered_at)?.toLocaleString(),
        lastVisitAt: new Date(v?.last_visit_at)?.toLocaleString(),
        visitCount: v?.visit_count
      }));

      return exportData;
    } catch (error) {
      console.error('Error exporting visitor data:', error);
      throw error;
    }
  }
};
